/**
 * RoleAssignmentDetailModal — Two-panel responsive role dossier.
 *
 * Left panel: role details (overview + details tabs).
 * Right panel: scrollable task list from Gantt schedule assignments.
 * Below 680px: collapses to single-column with "Role Details" / "Tasks" tabs.
 *
 * DEV-463: Role Lifecycle — Detail Modal
 * DEV-631: Role-Based Gantt Task Assignment
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { UserRoleAssignmentResponse } from '../../api/impactSignature';
import type { ProjectRoleAssignmentResponse } from '../../api/projectRoles';
import type { EngagementTask, TaskRoleAssignmentMap } from '../../api/ganttTaskRoleAssignments';
import { patchGanttTaskProgress, type GanttTaskResponse } from '../../api/ganttTasks';
import type { PreflightItem } from '../../Data/PreFlight/preflightTypes';
import { preflightStore, storeKey } from '../../Data/store';
import i18n from '../../i18n/config';
import { mediaSrc } from '../../api/mediaUrl';
import ExitReasonDialog from '../ProjectTeam/ExitReasonDialog';

/** Cast to bypass i18next v25's strict 0-arg type signature (per project convention). */
const tKpi = i18n.t.bind(i18n) as (key: string) => string;

/* ── Blocker detail (rich predecessor info) ─────────────── */

export interface BlockerDetail {
  taskId: number;
  taskName: string;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  assigneeName: string | null;
  /** Role label of the Responsible party for this blocking task (if known). */
  responsibleRoleLabel: string | null;
  /** Name of the person in the Responsible role (if known). */
  responsibleAssigneeName: string | null;
}
import styles from './RoleAssignmentDetailModal.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface RoleDetail {
  assignmentGuid: string;
  roleLabel: string;
  roleCategory: string;
  roleDescription: string | null;
  projectName: string | null;
  projectGuid: string | null;
  status: string;
  assignedAt: string;
  completedAt: string | null;
  expectedStartDate: string | null;
  expectedEndDate: string | null;
  durationType: string | null;
  verificationLevel: string | null;
  evidenceUrls: string[];
  evidenceNotes: string | null;
  completedVerifiedBy: string | null;
  completedVerifiedAt: string | null;
  instructions: string | null;
  notes: string | null;
  timeCommitment: string | null;
  skillsRequired: string[];
  invitedAt: string | null;
  invitationRespondedAt: string | null;
  invitationMessage: string | null;
  resignedAt: string | null;
  resignationReason: string | null;
  removedAt: string | null;
  visibility: string | null;
  impactAccrued: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userRole?: UserRoleAssignmentResponse | null;
  projectRole?: ProjectRoleAssignmentResponse | null;
  slotContext?: {
    roleLabel: string;
    roleCategory: string;
    description: string | null;
    verificationLevel: string;
    skillsRequired: string[];
    timeCommitment: string | null;
    durationType: string;
    expectedStartDate: string | null;
    expectedEndDate: string | null;
  } | null;
  projectName?: string;
  currentUserGuid?: string;
  onResign?: (assignmentGuid: string, reason: string) => void;
  /** Engagement tasks assigned to this role (fetched by parent). */
  tasks?: EngagementTask[];
  /** Hero thumbnail URL for the project/entity (from Engagements enrichment). */
  heroUrl?: string | null;
  /** Reporting hierarchy resolved from team structure. */
  hierarchy?: {
    reportsTo: { roleLabel: string; assigneeName: string | null } | null;
    directReports: { roleLabel: string; assigneeName: string | null }[];
  } | null;
  /** Full project Gantt task list for predecessor lookups (blocked-by detail). */
  allProjectGanttTasks?: GanttTaskResponse[];
  /** User display-name lookup (guid→name) for resolving assignees. */
  userDisplayNames?: Map<string, string>;
  /** All task-role assignments for the project, indexed by ganttTaskId (uppercase). For blocker role resolution. */
  allTaskRoleAssignments?: TaskRoleAssignmentMap;
  /** @deprecated — preflight items are now read directly from the preflightStore via the resolved projectGuid. */
  preflightItems?: PreflightItem[];
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fullDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function shortDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Status-semantic color for progress percentage. */
function progressColor(pct: number): string {
  if (pct === 100) return 'var(--info-text, #60a5fa)';      // blue = done
  if (pct >= 50) return 'var(--success-text, #4ade80)';     // green = on track
  if (pct > 0) return 'var(--warning-text, #fbbf24)';       // yellow = in progress
  return 'var(--text-muted, rgba(148,163,184,0.3))';        // grey = not started
}

/**
 * Canonical RACI color mapping — matches Gantt badges, filter chips, and task cards.
 * R = green (success), A = blue (info/accent), C = yellow (warning), I = grey (muted).
 */
function raciColor(type: string): string {
  switch (type) {
    case 'responsible': return 'var(--color-success, #22c55e)';
    case 'accountable': return 'var(--info-text, #60a5fa)';
    case 'consulted': return 'var(--color-warning, #f59e0b)';
    case 'informed': return 'var(--text-muted, #64748b)';
    default: return 'var(--text-muted, #64748b)';
  }
}

function buildDetail(props: Props): RoleDetail | null {
  const { userRole, projectRole, slotContext, projectName } = props;

  if (userRole) {
    return {
      assignmentGuid: userRole.assignmentGuid,
      roleLabel: userRole.roleLabel,
      roleCategory: userRole.roleCategory,
      roleDescription: userRole.roleDescription ?? null,
      projectName: userRole.projectName,
      projectGuid: userRole.projectGuid,
      status: userRole.status,
      assignedAt: userRole.assignedAt,
      completedAt: userRole.completedAt,
      expectedStartDate: userRole.expectedStartDate,
      expectedEndDate: userRole.expectedEndDate,
      durationType: userRole.durationType,
      verificationLevel: userRole.verificationLevel,
      evidenceUrls: userRole.evidenceUrls ?? [],
      evidenceNotes: userRole.evidenceNotes ?? null,
      completedVerifiedBy: userRole.completedVerifiedBy ?? null,
      completedVerifiedAt: userRole.completedVerifiedAt ?? null,
      instructions: userRole.instructions,
      notes: userRole.notes,
      timeCommitment: userRole.timeCommitment,
      skillsRequired: userRole.skillsRequired ?? [],
      invitedAt: userRole.invitedAt,
      invitationRespondedAt: userRole.invitationRespondedAt,
      invitationMessage: null,
      resignedAt: userRole.resignedAt,
      resignationReason: userRole.resignationReason,
      removedAt: null,
      visibility: null,
      impactAccrued: userRole.impactAccrued,
    };
  }

  if (projectRole && slotContext) {
    return {
      assignmentGuid: projectRole.assignmentGuid,
      roleLabel: slotContext.roleLabel,
      roleCategory: slotContext.roleCategory,
      roleDescription: slotContext.description ?? null,
      projectName: projectName ?? null,
      projectGuid: null,
      status: projectRole.status,
      assignedAt: projectRole.assignedAt,
      completedAt: projectRole.completedAt,
      expectedStartDate: projectRole.expectedStartDate ?? slotContext.expectedStartDate,
      expectedEndDate: projectRole.expectedEndDate ?? slotContext.expectedEndDate,
      durationType: slotContext.durationType,
      verificationLevel: slotContext.verificationLevel,
      evidenceUrls: projectRole.evidenceUrls ?? [],
      evidenceNotes: projectRole.evidenceNotes ?? null,
      completedVerifiedBy: null,
      completedVerifiedAt: null,
      instructions: projectRole.instructions,
      notes: projectRole.notes,
      timeCommitment: slotContext.timeCommitment,
      skillsRequired: slotContext.skillsRequired ?? [],
      invitedAt: projectRole.invitedAt,
      invitationRespondedAt: projectRole.invitationRespondedAt,
      invitationMessage: projectRole.invitationMessage,
      resignedAt: projectRole.resignedAt,
      resignationReason: projectRole.resignationReason,
      removedAt: projectRole.removedAt,
      visibility: projectRole.visibility,
      impactAccrued: projectRole.impactAccrued,
    };
  }

  return null;
}

/* ------------------------------------------------------------------ */
/*  Status class helpers                                               */
/* ------------------------------------------------------------------ */

function statusClass(status: string): string {
  switch (status) {
    case 'active': return styles.statusActive;
    case 'pending_verification':
    case 'pending_escalated_review':
    case 'pending_approval': return styles.statusPending;
    case 'pending_invitation': return styles.statusInvited;
    case 'completed': return styles.statusCompleted;
    case 'resigned': return styles.statusResigned;
    default: return styles.statusTerminal;
  }
}

function verificationDotColor(level: string): string {
  switch (level) {
    case 'auto': return 'var(--text-muted, #64748b)';
    case 'quick_confirm': return 'var(--info-text, #60a5fa)';
    case 'standard': return 'var(--warning-text, #fbbf24)';
    case 'evidence_required': return 'var(--accent-secondary, #a855f7)';
    default: return 'var(--text-muted, #64748b)';
  }
}

/* ------------------------------------------------------------------ */
/*  Responsive hook                                                    */
/* ------------------------------------------------------------------ */

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    try { return window.matchMedia(query).matches; } catch { return false; }
  });
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/* ------------------------------------------------------------------ */
/*  Progress Ring SVG                                                  */
/* ------------------------------------------------------------------ */

const ProgressRing: React.FC<{
  percent: number;
  size?: number;
  stroke?: number;
}> = ({ percent, size = 36, stroke = 3.5 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = progressColor(percent);

  return (
    <svg
      width={size}
      height={size}
      className={styles.progressRing}
      aria-label={`${percent}%`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--border-subtle, rgba(148,163,184,0.12))"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.4s ease' }}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: percent === 100 ? '9px' : '10px', fontWeight: 600, fill: color }}
      >
        {percent}%
      </text>
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Task Card                                                          */
/* ------------------------------------------------------------------ */

const TaskCard: React.FC<{
  task: EngagementTask;
  isSelected: boolean;
  isBlocked: boolean;
  onClick: () => void;
  t: (key: string) => string;
}> = ({ task, isSelected, isBlocked, onClick, t }) => {
  const isDone = task.percentComplete === 100;

  return (
    <div
      className={`${styles.taskCard} ${isSelected ? styles.taskCardSelected : ''} ${isBlocked ? styles.taskCardBlocked : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <ProgressRing percent={task.percentComplete} />

      <div className={styles.taskCardBody}>
        <div className={`${styles.taskName} ${isDone ? styles.taskNameDone : ''}`}>
          {task.taskName}
        </div>
        <div className={styles.taskMeta}>
          <span className={styles.taskDates}>
            {shortDate(task.startDate)} — {shortDate(task.endDate)}
          </span>
          <div
            className={styles.taskStatusDot}
            style={{ background: progressColor(task.percentComplete) }}
          />
        </div>
      </div>

      {/* Right-justified badges */}
      <div className={styles.taskCardBadges}>
        {isBlocked && (
          <span className={styles.blockedBadge}>
            {t('roleDetail.filterBlocked')}
          </span>
        )}
        <span
          className={styles.taskRaciBadge}
          style={{
            color: raciColor(task.responsibilityType),
            background: `color-mix(in srgb, ${raciColor(task.responsibilityType)} 10%, transparent)`,
          }}
        >
          {t(`gantt.${task.responsibilityType}`)}
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

type TaskFilter = 'all' | 'current' | 'upcoming' | 'done';
type RaciType = 'responsible' | 'accountable' | 'consulted' | 'informed';
const ALL_RACI: RaciType[] = ['responsible', 'accountable', 'consulted', 'informed'];
const RACI_LABELS: Record<RaciType, { short: string; labelKey: string }> = {
  responsible:  { short: 'R', labelKey: 'roleDetail.raciR' },
  accountable:  { short: 'A', labelKey: 'roleDetail.raciA' },
  consulted:    { short: 'C', labelKey: 'roleDetail.raciC' },
  informed:     { short: 'I', labelKey: 'roleDetail.raciI' },
};

const RoleAssignmentDetailModal: React.FC<Props> = (props) => {
  const { isOpen, onClose, currentUserGuid, onResign, tasks = [], heroUrl, hierarchy, allProjectGanttTasks = [], userDisplayNames, allTaskRoleAssignments } = props;
  const { t } = useAppTranslation('pages');

  const [showResignDialog, setShowResignDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'details'>('overview');
  const [mobilePanel, setMobilePanel] = useState<'role' | 'tasks'>('role');
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('current');
  const [raciFilter, setRaciFilter] = useState<Set<RaciType>>(new Set(['responsible']));
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // ── Editable peek state ──
  const [peekPercent, setPeekPercent] = useState(0);
  const [peekComment, setPeekComment] = useState('');
  const [peekSaving, setPeekSaving] = useState(false);
  const [peekSaved, setPeekSaved] = useState(false);
  const peekSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCompact = useMediaQuery('(max-width: 680px)');

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setActiveTab('overview');
      setMobilePanel('role');
      setTaskFilter('current');
      setSelectedTaskId(null);
      setPeekPercent(0);
      setPeekComment('');
      setPeekSaving(false);
      setPeekSaved(false);

      // Smart RACI default: R for own-role, all for other-role
      const ownerGuid = (props.userRole?.userGuid || props.projectRole?.userGuid || '').toUpperCase();
      const isOwn = currentUserGuid && ownerGuid && currentUserGuid.toUpperCase() === ownerGuid;
      setRaciFilter(isOwn ? new Set<RaciType>(['responsible']) : new Set<RaciType>(ALL_RACI));
    }
    return () => {
      if (peekSavedTimer.current) clearTimeout(peekSavedTimer.current);
    };
  }, [isOpen, currentUserGuid, props.userRole, props.projectRole]);

  // Sync peek editable state when selected task changes
  useEffect(() => {
    if (!selectedTaskId) return;
    const match = tasks.find((t) => t.ganttTaskId === selectedTaskId);
    if (match) {
      setPeekPercent(match.percentComplete);
      setPeekComment('');
      setPeekSaved(false);
    }
  }, [selectedTaskId, tasks]);

  // ── Hooks that must be above the early return (Rules of Hooks) ──

  // Today's date string for current/upcoming filtering
  const todayStr = React.useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // Build taskId (sequential number) → GanttTaskResponse lookup for predecessor resolution
  const ganttByTaskId = React.useMemo(() => {
    const map = new Map<number, GanttTaskResponse>();
    for (const gt of allProjectGanttTasks) {
      if (gt.taskId != null) map.set(gt.taskId, gt);
    }
    return map;
  }, [allProjectGanttTasks]);

  /**
   * Determine if a task is "blocked" — Option B: only flag when the task's
   * own startDate ≤ today AND at least one predecessor is incomplete.
   * This surfaces real actionable friction (tasks you'd be working on but
   * can't) without flooding the list with future tasks whose predecessors
   * haven't started yet.
   */
  const getBlockers = useCallback((task: EngagementTask): BlockerDetail[] => {
    if (!task.predecessors) return [];
    // Option B gate: only consider a task blocked if it should have started
    if (task.startDate && task.startDate > todayStr) return [];
    const predIds = task.predecessors.split(',').map((s) => s.trim()).filter(Boolean);
    const blockers: BlockerDetail[] = [];
    for (const rawId of predIds) {
      // predecessors may include dependency type suffix (e.g. "3FS", "5SS+2d") — extract the numeric part
      const numMatch = rawId.match(/^(\d+)/);
      if (!numMatch) continue;
      const predTaskId = Number(numMatch[1]);
      const predGantt = ganttByTaskId.get(predTaskId);
      if (!predGantt) continue;
      // Only flag as blocker if predecessor is not complete
      if (predGantt.percentComplete >= 100) continue;

      // Resolve Gantt-level assignee (resource field)
      const assigneeName = predGantt.assignedTo && userDisplayNames
        ? userDisplayNames.get(predGantt.assignedTo.toUpperCase()) ?? null
        : null;

      // Resolve the Responsible role for this blocking task via task-role assignments
      let responsibleRoleLabel: string | null = null;
      let responsibleAssigneeName: string | null = null;
      if (allTaskRoleAssignments) {
        const taskRoles = allTaskRoleAssignments.get(predGantt.id.toUpperCase());
        if (taskRoles) {
          const responsible = taskRoles.find((r) => r.responsibilityType === 'responsible');
          if (responsible) {
            responsibleRoleLabel = responsible.roleLabel ?? null;
            // Resolve the name of the person filling that role slot
            // The assignment's roleSlotGuid links to a slot which has assignments
            // But the enriched response already has roleLabel; for the person,
            // we fall back to the Gantt assignee or the userDisplayNames if we had a userGuid on the assignment
            responsibleAssigneeName = assigneeName; // Best available: Gantt resource
          }
        }
      }

      blockers.push({
        taskId: predTaskId,
        taskName: predGantt.taskName,
        startDate: predGantt.startDate,
        endDate: predGantt.endDate,
        percentComplete: predGantt.percentComplete,
        assigneeName,
        responsibleRoleLabel,
        responsibleAssigneeName,
      });
    }
    return blockers;
  }, [ganttByTaskId, userDisplayNames, allTaskRoleAssignments, todayStr]);

  /* ── Save handler for editable peek ── */
  const handlePeekSave = useCallback(async (taskGuid: string, percent: number, comment: string) => {
    setPeekSaving(true);
    try {
      await patchGanttTaskProgress(taskGuid, percent, comment || undefined);
      // Optimistic local update — mutate the tasks array in-place for immediate feedback
      const target = tasks.find((t) => t.ganttTaskId === taskGuid);
      if (target) {
        target.percentComplete = percent;
        if (comment) target.notes = comment;
      }
      setPeekSaved(true);
      if (peekSavedTimer.current) clearTimeout(peekSavedTimer.current);
      peekSavedTimer.current = setTimeout(() => setPeekSaved(false), 2500);
    } catch (err) {
      console.error('[RoleDetail] Failed to save progress:', err);
    } finally {
      setPeekSaving(false);
    }
  }, [tasks]);

  const detail = buildDetail(props);

  /* DEV-648: Read preflight items directly from the store using the resolved project GUID.
   * This avoids threading a prop from 3 different parent call sites. */
  const preflightItems: PreflightItem[] = useMemo(() => {
    if (!detail?.projectGuid) return [];
    return preflightStore.get(storeKey(detail.projectGuid)) || [];
  }, [detail?.projectGuid]);

  if (!isOpen || !detail) return null;

  // Derived task stats
  const completedCount = tasks.filter((t) => t.percentComplete === 100).length;
  const inProgressCount = tasks.filter((t) => t.percentComplete > 0 && t.percentComplete < 100).length;
  const notStartedCount = tasks.filter((t) => t.percentComplete === 0).length;
  const avgProgress = tasks.length > 0
    ? Math.round(tasks.reduce((sum, t) => sum + t.percentComplete, 0) / tasks.length)
    : 0;

  // Filtered tasks: RACI filter × temporal filter
  const raciFilterAll = raciFilter.size === ALL_RACI.length;
  const filteredTasks = tasks.filter((task) => {
    // RACI filter (skip if all selected)
    if (!raciFilterAll && !raciFilter.has(task.responsibilityType as RaciType)) return false;

    // Temporal filter
    if (taskFilter === 'done') return task.percentComplete === 100;
    if (taskFilter === 'current') {
      if (task.percentComplete === 100) return false;
      if (!task.startDate) return true;
      return task.startDate <= todayStr;
    }
    if (taskFilter === 'upcoming') {
      if (task.percentComplete === 100) return false;
      if (!task.startDate) return false;
      return task.startDate > todayStr;
    }
    return true;
  });

  /**
   * Sort by temporal priority (urgency-first):
   *
   * Bucket 0 — Blocked: has incomplete predecessors. Sort by endDate asc
   *            (most overdue blocker first — drives follow-up urgency).
   * Bucket 1 — Current: startDate ≤ today and not done. Sort by endDate
   *            asc so the most overdue / closest-deadline tasks float up.
   *            Within the same endDate, higher % sorts first (closer to
   *            completion = keep momentum).
   * Bucket 2 — Upcoming: startDate > today and not done. Sort by
   *            startDate asc (what's coming soonest).
   * Bucket 3 — Done: percentComplete === 100. Sort by endDate desc
   *            (most recently completed first for context relevance).
   *
   * Tiebreakers: milestones before regular tasks, then alphabetical.
   */
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const aBlocked = getBlockers(a).length > 0;
    const bBlocked = getBlockers(b).length > 0;
    const aDone = a.percentComplete === 100;
    const bDone = b.percentComplete === 100;
    const aCurrent = !aDone && (!a.startDate || a.startDate <= todayStr);
    const bCurrent = !bDone && (!b.startDate || b.startDate <= todayStr);

    const bucket = (blocked: boolean, done: boolean, current: boolean): number => {
      if (!done && blocked) return 0;
      if (current) return 1;
      if (!done) return 2;
      return 3;
    };

    const aBucket = bucket(aBlocked, aDone, aCurrent);
    const bBucket = bucket(bBlocked, bDone, bCurrent);

    if (aBucket !== bBucket) return aBucket - bBucket;

    // Within Blocked and Current: endDate ascending (most overdue first)
    if (aBucket <= 1) {
      const aEnd = a.endDate ?? '9999';
      const bEnd = b.endDate ?? '9999';
      if (aEnd !== bEnd) return aEnd < bEnd ? -1 : 1;
      // Same endDate: higher % first (closer to done = keep momentum)
      if (a.percentComplete !== b.percentComplete) return b.percentComplete - a.percentComplete;
    }
    // Within Upcoming: startDate ascending (soonest first)
    else if (aBucket === 2) {
      const aStart = a.startDate ?? '9999';
      const bStart = b.startDate ?? '9999';
      if (aStart !== bStart) return aStart < bStart ? -1 : 1;
    }
    // Within Done: endDate descending (most recently completed first)
    else {
      const aEnd = a.endDate ?? '0000';
      const bEnd = b.endDate ?? '0000';
      if (aEnd !== bEnd) return aEnd > bEnd ? -1 : 1;
    }

    // Tiebreaker: milestones before regular tasks
    if (a.isMilestone !== b.isMilestone) return a.isMilestone ? -1 : 1;

    // Final tiebreaker: alphabetical
    return a.taskName.localeCompare(b.taskName);
  });

  const selectedTask = selectedTaskId ? tasks.find((t) => t.ganttTaskId === selectedTaskId) : null;

  // Permission checks
  const hasInvitation = detail.invitedAt != null;
  const hasExit = detail.resignedAt != null || detail.removedAt != null;
  const hasEvidence = detail.evidenceUrls.length > 0 || detail.evidenceNotes;

  const isOwnRole = currentUserGuid &&
    detail.assignmentGuid &&
    currentUserGuid.toUpperCase() === (props.userRole?.userGuid || props.projectRole?.userGuid || '').toUpperCase();
  const canResign = isOwnRole && detail.status === 'active' && !!onResign;

  /* ── RACI chip toggle helper ── */
  const toggleRaci = (type: RaciType) => {
    setRaciFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Don't allow deselecting all — keep at least one
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  /* ── Filter chip helper ── */
  const filterKeys: { key: TaskFilter; labelKey: string }[] = [
    { key: 'all', labelKey: 'roleDetail.filterAll' },
    { key: 'current', labelKey: 'roleDetail.filterCurrent' },
    { key: 'upcoming', labelKey: 'roleDetail.filterUpcoming' },
    { key: 'done', labelKey: 'roleDetail.filterDone' },
  ];

  /* ────────────────────────────────────────────────────── */
  /*  Left Panel Content                                    */
  /* ────────────────────────────────────────────────────── */

  const renderOverviewTab = () => (
    <>
      {/* Progress card — only show if we have tasks */}
      {tasks.length > 0 && (
        <div className={styles.progressCard}>
          <div className={styles.progressCardTitle}>{t('roleDetail.roleProgress')}</div>

          <div className={styles.progressBarRow}>
            <span className={styles.progressBarLabel}>{t('roleDetail.overallCompletion')}</span>
            <span
              className={styles.progressBarValue}
              style={{ color: avgProgress >= 50 ? 'var(--success-text, #4ade80)' : 'var(--warning-text, #fbbf24)' }}
            >
              {avgProgress}%
            </span>
          </div>
          <div className={styles.progressBarTrack}>
            <div
              className={`${styles.progressBarFill} ${avgProgress >= 50 ? styles.progressBarFillGreen : styles.progressBarFillYellow}`}
              style={{ width: `${avgProgress}%` }}
            />
          </div>

          <div className={styles.miniStats}>
            <div className={styles.miniStat}>
              <div className={styles.miniStatValue} style={{ color: 'var(--info-text, #60a5fa)' }}>{completedCount}</div>
              <div className={styles.miniStatLabel}>{t('roleDetail.statComplete')}</div>
            </div>
            <div className={styles.miniStat}>
              <div className={styles.miniStatValue} style={{ color: 'var(--success-text, #4ade80)' }}>{inProgressCount}</div>
              <div className={styles.miniStatLabel}>{t('roleDetail.statInProgress')}</div>
            </div>
            <div className={styles.miniStat}>
              <div className={styles.miniStatValue} style={{ color: 'var(--text-muted, #64748b)' }}>{notStartedCount}</div>
              <div className={styles.miniStatLabel}>{t('roleDetail.statNotStarted')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Dates & Duration */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionTitle}>{t('roleDetail.sectionDates')}</div>
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{t('roleDetail.assignedAt')}</span>
          <span className={styles.fieldValue}>{fullDate(detail.assignedAt)}</span>
        </div>
        {detail.expectedStartDate && (
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{t('roleDetail.expectedStart')}</span>
            <span className={styles.fieldValue}>{fullDate(detail.expectedStartDate)}</span>
          </div>
        )}
        {detail.expectedEndDate && (
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{t('roleDetail.expectedEnd')}</span>
            <span className={styles.fieldValue}>{fullDate(detail.expectedEndDate)}</span>
          </div>
        )}
        {detail.completedAt && (
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{t('roleDetail.completedAt')}</span>
            <span className={styles.fieldValue}>{fullDate(detail.completedAt)}</span>
          </div>
        )}
        {detail.timeCommitment && (
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{t('roleDetail.timeCommitment')}</span>
            <span className={styles.fieldValue}>{detail.timeCommitment}</span>
          </div>
        )}
      </div>

      {/* Reporting hierarchy */}
      {hierarchy && (hierarchy.reportsTo || hierarchy.directReports.length > 0) && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionHierarchy')}</div>
          {hierarchy.reportsTo && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>{t('roleDetail.reportsTo')}</span>
              <span className={styles.fieldValue}>
                {hierarchy.reportsTo.roleLabel}
                {hierarchy.reportsTo.assigneeName && (
                  <span className={styles.hierarchyPerson}> — {hierarchy.reportsTo.assigneeName}</span>
                )}
              </span>
            </div>
          )}
          {hierarchy.directReports.length > 0 && (
            <>
              <div className={styles.fieldRow} style={{ marginBlockStart: '4px' }}>
                <span className={styles.fieldLabel}>{t('roleDetail.directReports')}</span>
                <span className={styles.fieldValue}>{hierarchy.directReports.length}</span>
              </div>
              <div className={styles.hierarchyList}>
                {hierarchy.directReports.map((dr, i) => (
                  <div key={i} className={styles.hierarchyItem}>
                    <span className={styles.hierarchyRole}>{dr.roleLabel}</span>
                    {dr.assigneeName && (
                      <span className={styles.hierarchyPerson}>{dr.assigneeName}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Description */}
      {detail.roleDescription && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionDescription')}</div>
          <div className={styles.textBlock}>{detail.roleDescription}</div>
        </div>
      )}

      {/* Skills */}
      {detail.skillsRequired.length > 0 && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionSkills')}</div>
          <div className={styles.tagRow}>
            {detail.skillsRequired.map((s) => (
              <span key={s} className={styles.tag}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      {detail.instructions && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionInstructions')}</div>
          <div className={styles.textBlock}>{detail.instructions}</div>
        </div>
      )}

      {/* Notes */}
      {detail.notes && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionNotes')}</div>
          <div className={styles.textBlock}>{detail.notes}</div>
        </div>
      )}
    </>
  );

  const renderDetailsTab = () => (
    <>
      {/* Verification */}
      {detail.verificationLevel && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionVerification')}</div>
          <div className={styles.verificationBadge}>
            <div
              className={styles.verificationDot}
              style={{ background: verificationDotColor(detail.verificationLevel!) }}
            />
            <span>{t(`roleDetail.verificationLevels.${detail.verificationLevel}`)}</span>
          </div>
          {detail.completedVerifiedAt && (
            <div className={styles.fieldRow} style={{ marginTop: '8px' }}>
              <span className={styles.fieldLabel}>{t('roleDetail.verifiedAt')}</span>
              <span className={styles.fieldValue}>{shortDateTime(detail.completedVerifiedAt)}</span>
            </div>
          )}
        </div>
      )}

      {/* Duration Type */}
      {detail.durationType && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.durationType')}</div>
          <span className={styles.durationBadge}>
            {t(`roleDetail.durationTypes.${detail.durationType}`)}
          </span>
        </div>
      )}

      {/* Evidence */}
      {hasEvidence && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionEvidence')}</div>
          {detail.evidenceUrls.map((url, i) => (
            <a key={i} className={styles.evidenceLink} href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          ))}
          {detail.evidenceNotes && (
            <div className={styles.textBlock}>{detail.evidenceNotes}</div>
          )}
        </div>
      )}

      {/* Invitation */}
      {hasInvitation && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionInvitation')}</div>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{t('roleDetail.invitedAt')}</span>
            <span className={styles.fieldValue}>{shortDateTime(detail.invitedAt)}</span>
          </div>
          {detail.invitationRespondedAt && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>{t('roleDetail.respondedAt')}</span>
              <span className={styles.fieldValue}>{shortDateTime(detail.invitationRespondedAt)}</span>
            </div>
          )}
          {detail.invitationMessage && (
            <div className={styles.textBlock}>{detail.invitationMessage}</div>
          )}
        </div>
      )}

      {/* Exit */}
      {hasExit && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionExit')}</div>
          {detail.resignedAt && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>{t('roleDetail.resignedAt')}</span>
              <span className={styles.fieldValue}>{shortDateTime(detail.resignedAt)}</span>
            </div>
          )}
          {detail.removedAt && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>{t('roleDetail.removedAt')}</span>
              <span className={styles.fieldValue}>{shortDateTime(detail.removedAt)}</span>
            </div>
          )}
          {detail.resignationReason && (
            <div className={styles.textBlock}>{detail.resignationReason}</div>
          )}
        </div>
      )}

      {/* Visibility */}
      {detail.visibility && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>{t('roleDetail.sectionVisibility')}</div>
          <span className={styles.visibilityBadge}>
            {t(`roleDetail.visibilityOptions.${detail.visibility}`)}
          </span>
        </div>
      )}

      {/* RACI Legend */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionTitle}>RACI</div>
        <div className={styles.raciList}>
          {(['responsible', 'accountable', 'consulted', 'informed'] as const).map((r) => (
            <div key={r} className={styles.raciRow}>
              <div className={styles.raciDot} style={{ background: raciColor(r) }} />
              <span className={styles.raciLabel}>{t(`gantt.${r}`)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  /* ────────────────────────────────────────────────────── */
  /*  Right Panel Content (Task List)                       */
  /* ────────────────────────────────────────────────────── */

  const renderTaskPanel = () => {
    const selectedBlockers: BlockerDetail[] = selectedTask ? getBlockers(selectedTask) : [];

    const handleMarkComplete = () => {
      if (!selectedTask) return;
      setPeekPercent(100);
      handlePeekSave(selectedTask.ganttTaskId, 100, peekComment);
    };

    return (
      <>
        {/* Task panel header */}
        <div className={styles.taskHeader}>
          {/* Row 1: Title + count */}
          <div className={styles.taskHeaderRow}>
            <h3 className={styles.taskHeaderTitle}>
              {t('roleDetail.assignedTasks')}
              <span className={styles.taskHeaderCount}>
                {sortedTasks.length < tasks.length
                  ? t('roleDetail.tasksFiltered', { shown: sortedTasks.length, total: tasks.length })
                  : t('roleDetail.tasksFromSchedule', { count: tasks.length })}
              </span>
            </h3>
          </div>

          {/* Row 2: RACI chips (left) + temporal chips (right) */}
          <div className={styles.filterRow}>
            <div className={styles.raciChipRow}>
              {ALL_RACI.map((raci) => (
                <button
                  key={raci}
                  type="button"
                  className={`${styles.raciChip} ${raciFilter.has(raci) ? styles[`raciChip_${raci}`] : ''}`}
                  onClick={() => toggleRaci(raci)}
                  title={t(RACI_LABELS[raci].labelKey)}
                >
                  {RACI_LABELS[raci].short}
                </button>
              ))}
            </div>

            <div className={styles.filterChips}>
              {filterKeys.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`${styles.filterChip} ${taskFilter === f.key ? styles.filterChipActive : ''}`}
                  onClick={() => setTaskFilter(f.key)}
                >
                  {t(f.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Task list */}
        {sortedTasks.length > 0 ? (
          <div className={styles.taskList}>
            {sortedTasks.map((task) => (
              <TaskCard
                key={task.ganttTaskId}
                task={task}
                isSelected={selectedTaskId === task.ganttTaskId}
                isBlocked={getBlockers(task).length > 0}
                onClick={() => setSelectedTaskId(
                  selectedTaskId === task.ganttTaskId ? null : task.ganttTaskId
                )}
                t={t}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateTitle}>{t('roleDetail.noTasks')}</div>
            <div className={styles.emptyStateHint}>{t('roleDetail.noTasksHint')}</div>
          </div>
        )}

        {/* ── Editable task peek (bottom tray) ── */}
        {selectedTask && (
          <div className={styles.taskPeek}>
            <div className={styles.taskPeekHeader}>
              <span className={styles.taskPeekName}>{selectedTask.taskName}</span>
              <button
                type="button"
                className={styles.taskPeekClose}
                onClick={() => setSelectedTaskId(null)}
              >
                ×
              </button>
            </div>

            {/* Date + progress info row */}
            <div className={styles.taskPeekDetails}>
              <span>
                {t('roleDetail.taskStart')}: <strong className={styles.taskPeekValue}>{fullDate(selectedTask.startDate)}</strong>
              </span>
              <span>
                {t('roleDetail.taskEnd')}: <strong className={styles.taskPeekValue}>{fullDate(selectedTask.endDate)}</strong>
              </span>
            </div>

            {/* Blocked-by detail */}
            {selectedBlockers.length > 0 && (
              <div className={styles.blockedSection}>
                <div className={styles.blockedSectionHeader}>
                  <span className={styles.blockedWarningIcon}>⚠</span>
                  <span>{t('roleDetail.blockedBy')}</span>
                </div>
                {selectedBlockers.map((b) => (
                  <div key={b.taskId} className={styles.blockerRow}>
                    <div className={styles.blockerNameRow}>
                      <ProgressRing percent={b.percentComplete} size={28} stroke={3} />
                      <span className={styles.blockerName}>{b.taskName}</span>
                    </div>
                    <div className={styles.blockerMeta}>
                      <span>{shortDate(b.startDate)} — {shortDate(b.endDate)}</span>
                      <span className={styles.blockerMetaDivider}>·</span>
                      <span style={{ color: progressColor(b.percentComplete), fontWeight: 600 }}>
                        {b.percentComplete}%
                      </span>
                      {(b.responsibleRoleLabel || b.assigneeName) && (
                        <>
                          <span className={styles.blockerMetaDivider}>·</span>
                          <span className={styles.blockerAssignee}>
                            {b.responsibleRoleLabel && (
                              <span className={styles.blockerRoleBadge}>{b.responsibleRoleLabel}</span>
                            )}
                            {b.assigneeName && <span>{b.assigneeName}</span>}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── DEV-648: Linked Resources (read-only cross-reference) ── */}
            {(() => {
              // Find preflight items linked to this task via gantt_task_id
              const taskGanttId = selectedTask.ganttTaskId;
              const linked = taskGanttId
                ? preflightItems.filter(
                    (p) => p.gantt_task_id && p.gantt_task_id.toUpperCase() === String(taskGanttId).toUpperCase()
                  )
                : [];

              if (linked.length === 0) return null;

              const metCount = linked.filter((p) => p.state === 'met').length;
              const allMet = metCount === linked.length;
              const hasBlocked = linked.some((p) => p.state === 'blocked');

              const summaryLabel = allMet
                ? t('roleDetail.allResourcesAcquired')
                : hasBlocked
                ? t('roleDetail.resourcesBlocked')
                : t('roleDetail.resourcesAcquiredCount', { met: String(metCount), total: String(linked.length) });
              const summaryColor = allMet
                ? 'var(--success-text, #4ade80)'
                : hasBlocked
                ? 'var(--error-text, #f87171)'
                : 'var(--warning-text, #fbbf24)';
              const summaryBg = allMet
                ? 'rgba(74, 222, 128, 0.12)'
                : hasBlocked
                ? 'rgba(239, 68, 68, 0.12)'
                : 'rgba(251, 191, 36, 0.12)';

              const STATE_BADGE: Record<string, { bg: string; color: string; label: string }> = {
                met:     { bg: 'rgba(74,222,128,0.12)', color: 'var(--success-text, #4ade80)', label: tKpi('kpi:preflight.states.met') },
                seeking: { bg: 'rgba(251,191,36,0.12)', color: 'var(--warning-text, #fbbf24)', label: tKpi('kpi:preflight.states.seeking') },
                pledged: { bg: 'rgba(96,165,250,0.12)', color: 'var(--info-text, #60a5fa)', label: tKpi('kpi:preflight.states.pledged') },
                blocked: { bg: 'rgba(239,68,68,0.12)', color: 'var(--error-text, #f87171)', label: tKpi('kpi:preflight.states.blocked') },
                draft:   { bg: 'var(--bg-hover, rgba(255,255,255,0.04))', color: 'var(--text-muted, #64748b)', label: tKpi('kpi:preflight.states.draft') },
                partial: { bg: 'rgba(251,191,36,0.12)', color: 'var(--warning-text, #fbbf24)', label: tKpi('kpi:preflight.states.partial') },
              };

              const CATEGORY_ICONS: Record<string, string> = {
                materials: '\u{1F4E6}', equipment: '\u{1F527}', personnel: '\u{1F464}', services: '\u{1F3E2}',
                permits: '\u{1F4CB}', funding: '\u{1F4B0}', technology: '\u{1F4BB}', facilities: '\u{1F3D7}',
                transport: '\u{1F69B}', community: '\u{1F91D}', data: '\u{1F4CA}', other: '\u{1F4CE}',
                labor: '\u{1F464}', transportation: '\u{1F69B}', admin_legal: '\u{1F4CB}', knowledge: '\u{1F4DA}',
                financial: '\u{1F4B0}', in_kind: '\u{1F91D}', software_tech: '\u{1F4BB}',
              };

              return (
                <div className={styles.linkedResourcesSection}>
                  <div className={styles.linkedResourcesHeader}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                    </svg>
                    <span>{t('roleDetail.linkedResources')}</span>
                    <span className={styles.linkedResourcesCount}>
                      {t('roleDetail.linkedResourcesCount', { count: String(linked.length) })}
                    </span>
                    <span
                      className={styles.linkedResourcesSummary}
                      style={{ color: summaryColor, background: summaryBg }}
                    >
                      {summaryLabel}
                    </span>
                  </div>
                  <div className={styles.linkedResourcesList}>
                    {linked.map((r) => {
                      const badge = STATE_BADGE[r.state] || STATE_BADGE.seeking;
                      return (
                        <div key={r.item_guid} className={styles.linkedResourceRow}>
                          <span className={styles.linkedResourceIcon}>
                            {CATEGORY_ICONS[r.category] || '\u{1F4CE}'}
                          </span>
                          <div className={styles.linkedResourceBody}>
                            <p
                              className={styles.linkedResourceName}
                              style={{
                                textDecoration: r.state === 'met' ? 'line-through' : 'none',
                                opacity: r.state === 'met' ? 0.7 : 1,
                              }}
                            >
                              {r.label}
                            </p>
                            {r.fulfillment_note && (
                              <p className={styles.linkedResourceNote}>
                                {r.fulfillment_note.split('\n')[0]}
                              </p>
                            )}
                          </div>
                          <span
                            className={styles.linkedResourceBadge}
                            style={{ color: badge.color, background: badge.bg }}
                          >
                            <span
                              className={styles.linkedResourceDot}
                              style={{ background: badge.color }}
                            />
                            {badge.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── RACI-gated controls ──
             * R (Responsible): slider + comments + Save
             * A (Accountable): slider + comments + Mark Complete + Save
             * C (Consulted):   comments only + Save
             * I (Informed):    read-only
             * Non-owner:       read-only
             */}
            {(() => {
              const raci = isOwnRole ? selectedTask.responsibilityType : null;
              const canUpdateProgress = raci === 'responsible' || raci === 'accountable';
              const canComment = raci === 'responsible' || raci === 'accountable' || raci === 'consulted';
              const canSignOff = raci === 'accountable';
              const hasBlockers = selectedBlockers.length > 0;

              // Informed or non-owner — fully read-only
              if (!canUpdateProgress && !canComment) {
                return (
                  <div className={styles.taskPeekReadOnly}>
                    <span>
                      {t('roleDetail.taskProgress')}: <strong style={{ color: progressColor(selectedTask.percentComplete), fontWeight: 600 }}>
                        {selectedTask.percentComplete}%
                      </strong>
                    </span>
                    {raci && (
                      <span className={styles.raciHint}>
                        {t(`gantt.${raci}`)}
                      </span>
                    )}
                  </div>
                );
              }

              // R, A, or C — show editable controls based on permission level
              return (
                <div className={styles.peekControls}>
                  {/* % complete slider — R and A only */}
                  {canUpdateProgress && (
                    <div className={styles.peekSliderRow}>
                      <label className={styles.peekSliderLabel}>
                        {t('roleDetail.updateProgress')}
                      </label>
                      <div className={styles.peekSliderGroup}>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={5}
                          value={peekPercent}
                          onChange={(e) => setPeekPercent(Number(e.target.value))}
                          className={styles.peekSlider}
                          disabled={peekSaving}
                        />
                        <span
                          className={styles.peekSliderValue}
                          style={{ color: progressColor(peekPercent) }}
                        >
                          {peekPercent}%
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Read-only progress for Consulted (no slider) */}
                  {!canUpdateProgress && (
                    <div className={styles.taskPeekReadOnly}>
                      <span>
                        {t('roleDetail.taskProgress')}: <strong style={{ color: progressColor(selectedTask.percentComplete), fontWeight: 600 }}>
                          {selectedTask.percentComplete}%
                        </strong>
                      </span>
                    </div>
                  )}

                  {/* Comment field — R, A, and C */}
                  {canComment && (
                    <div className={styles.peekCommentRow}>
                      <textarea
                        className={styles.peekTextarea}
                        placeholder={t('roleDetail.addComment')}
                        value={peekComment}
                        onChange={(e) => setPeekComment(e.target.value)}
                        rows={2}
                        disabled={peekSaving}
                      />
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className={styles.peekActions}>
                    {/* Mark Complete — Accountable only, disabled if blocked */}
                    {canSignOff && peekPercent < 100 && (
                      <button
                        type="button"
                        className={styles.peekMarkComplete}
                        onClick={handleMarkComplete}
                        disabled={peekSaving || hasBlockers}
                        title={hasBlockers ? t('roleDetail.blockedCannotComplete') : undefined}
                      >
                        {t('roleDetail.markComplete')}
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.peekSaveBtn}
                      onClick={() => handlePeekSave(selectedTask.ganttTaskId, peekPercent, peekComment)}
                      disabled={peekSaving}
                    >
                      {peekSaving ? '…' : peekSaved ? t('roleDetail.progressSaved') : t('roleDetail.saveProgress')}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </>
    );
  };

  /* ────────────────────────────────────────────────────── */
  /*  Render                                                */
  /* ────────────────────────────────────────────────────── */

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${isCompact ? styles.modalCompact : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={detail.roleLabel || detail.roleCategory}
      >
        {/* ── Modal-level close button (always top-right) ── */}
        <button type="button" className={styles.modalCloseBtn} onClick={onClose}>
          ×
        </button>

        {/* ── Compact: Mobile tab bar ── */}
        {isCompact && (
          <div className={styles.compactTabBar}>
            <button
              type="button"
              className={`${styles.compactTab} ${mobilePanel === 'role' ? styles.compactTabActive : ''}`}
              onClick={() => setMobilePanel('role')}
            >
              {t('roleDetail.tabRoleDetails')}
            </button>
            <button
              type="button"
              className={`${styles.compactTab} ${mobilePanel === 'tasks' ? styles.compactTabActive : ''}`}
              onClick={() => setMobilePanel('tasks')}
            >
              {t('roleDetail.tabTasks')} ({tasks.length})
            </button>
          </div>
        )}

        {/* ═══════ LEFT PANEL — Role Details ═══════ */}
        <div
          className={`
            ${styles.leftPanel}
            ${isCompact ? styles.leftPanelCompact : ''}
            ${isCompact && mobilePanel !== 'role' ? styles.panelHidden : ''}
          `}
        >
          {/* Header */}
          <div className={styles.header}>
            {/* Category pill — sits above the hero+title row */}
            <span className={styles.categoryPill}>{detail.roleCategory}</span>

            {/* Hero image + title block, vertically centered */}
            <div className={styles.headerTop}>
              {heroUrl ? (
                <img className={styles.heroThumb} src={mediaSrc(heroUrl) ?? undefined} alt={detail.projectName || ''} />
              ) : (
                <div className={styles.heroThumbFallback}>
                  {(detail.projectName || detail.roleLabel || '?').charAt(0).toUpperCase()}
                </div>
              )}

              <div className={styles.headerInfo}>
                <h2 className={styles.title}>{detail.roleLabel || detail.roleCategory}</h2>
                {detail.projectName && (
                  <p className={styles.projectName}>{detail.projectName}</p>
                )}
              </div>
            </div>

            {/* Status + summary stats */}
            <div className={styles.statusRow}>
              <span className={`${styles.statusBadge} ${statusClass(detail.status)}`}>
                {t(`impact.roleStatus.${detail.status}`)}
              </span>
              {tasks.length > 0 && (
                <>
                  <div className={styles.statusDivider} />
                  <span className={styles.statusStat}>
                    {completedCount}/{tasks.length} {t('roleDetail.tabTasks').toLowerCase()}
                  </span>
                  <div className={styles.statusDivider} />
                  <span className={styles.statusStat}>{avgProgress}% avg</span>
                </>
              )}
              {detail.impactAccrued && (
                <>
                  <div className={styles.statusDivider} />
                  <span className={styles.statusStat}>{t('roleDetail.impactAccrued')}</span>
                </>
              )}
            </div>
          </div>

          {/* Tab switcher */}
          <div className={styles.tabBar}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              {t('roleDetail.tabOverview')}
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'details' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('details')}
            >
              {t('roleDetail.tabDetails')}
            </button>
          </div>

          {/* Scrollable body */}
          <div className={styles.body}>
            {activeTab === 'overview' ? renderOverviewTab() : renderDetailsTab()}
          </div>

          {/* Footer */}
          {canResign && (
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.resignBtn}
                onClick={() => setShowResignDialog(true)}
              >
                {t('roleDetail.resignButton')}
              </button>
            </div>
          )}
        </div>

        {/* ═══════ RIGHT PANEL — Task List ═══════ */}
        <div
          className={`
            ${styles.rightPanel}
            ${isCompact ? styles.rightPanelCompact : ''}
            ${isCompact && mobilePanel !== 'tasks' ? styles.panelHidden : ''}
          `}
        >
          {renderTaskPanel()}
        </div>
      </div>

      {/* Resign confirmation dialog */}
      <ExitReasonDialog
        isOpen={showResignDialog}
        mode="resign"
        userName={t('roleDetail.resignYou')}
        roleName={detail.roleLabel || detail.roleCategory}
        onConfirm={(reason) => {
          setShowResignDialog(false);
          onResign?.(detail.assignmentGuid, reason);
        }}
        onCancel={() => setShowResignDialog(false)}
      />
    </div>
  );
};

export default RoleAssignmentDetailModal;
